import { useMemo, useState } from 'react';
import { Check, ChevronDown, Filter, ListFilter, Search } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/ui/popover';
import { cn } from '@/shared/lib/cn';

export type ClienteOpt = { clienteId: string; cliente: string };
export type EstadoFilter = 'vigentes' | 'anuladas' | 'todas';

/** Sentinel for "todos los vendedores" (single-select radios need a value). */
export const VENDEDOR_ALL = '__all__';

const ESTADOS: Array<{ value: EstadoFilter; label: string }> = [
  { value: 'vigentes', label: 'Vigentes' },
  { value: 'anuladas', label: 'Anuladas' },
  { value: 'todas', label: 'Todas' },
];

/**
 * "Filtrar" button + dropdown (same pattern as Ventas/Tesorería/Inventario) that
 * hosts the Cliente / Vendedor / Estado single-select filters. Búsqueda de venta
 * stays in the page toolbar (it's a free-text search, not a dropdown filter).
 */
export function FacturacionFilterPopover({
  clientes,
  vendedores,
  cliente,
  onClienteChange,
  vendedor,
  onVendedorChange,
  estado,
  onEstadoChange,
}: {
  clientes: ClienteOpt[];
  vendedores: string[];
  cliente: ClienteOpt | null;
  onClienteChange: (c: ClienteOpt | null) => void;
  vendedor: string;
  onVendedorChange: (v: string) => void;
  estado: EstadoFilter;
  onEstadoChange: (e: EstadoFilter) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const active =
    (cliente ? 1 : 0) + (vendedor !== VENDEDOR_ALL ? 1 : 0) + (estado !== 'vigentes' ? 1 : 0);

  // Cliente can grow long (every AR client that ever gets an ARCA factura), so
  // the section keeps its own inline search rather than a bare radio list.
  const clientesFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? clientes.filter((c) => c.cliente.toLowerCase().includes(q)) : clientes;
  }, [clientes, query]);

  const reset = () => {
    onClienteChange(null);
    onVendedorChange(VENDEDOR_ALL);
    onEstadoChange('vigentes');
  };

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (o) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button variant="outline" className={cn('h-12 gap-2', active > 0 && 'border-foreground/60')}>
          <Filter className="h-4 w-4" aria-hidden />
          Filtrar
          {active > 0 && <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />}
          <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 p-3">
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Cliente
          </p>
          <div className="flex items-center gap-2 rounded-md border bg-background px-2">
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar cliente…"
              className="h-8 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
            <FilterRadio
              label="Todos los clientes"
              checked={!cliente}
              onSelect={() => onClienteChange(null)}
            />
            {clientesFiltered.map((c) => (
              <FilterRadio
                key={c.clienteId}
                label={c.cliente}
                checked={cliente?.clienteId === c.clienteId}
                onSelect={() => onClienteChange(c)}
              />
            ))}
            {clientesFiltered.length === 0 && (
              <p className="px-2 py-3 text-center text-xs text-muted-foreground">Sin resultados.</p>
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Vendedor
          </p>
          <div className="flex max-h-40 flex-col gap-1 overflow-y-auto">
            <FilterRadio
              label="Todos los vendedores"
              checked={vendedor === VENDEDOR_ALL}
              onSelect={() => onVendedorChange(VENDEDOR_ALL)}
            />
            {vendedores.map((v) => (
              <FilterRadio
                key={v}
                label={v}
                checked={vendedor === v}
                onSelect={() => onVendedorChange(v)}
              />
            ))}
          </div>
        </div>

        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Estado
          </p>
          <div className="flex flex-col gap-1">
            {ESTADOS.map((e) => (
              <FilterRadio
                key={e.value}
                label={e.label}
                checked={estado === e.value}
                onSelect={() => onEstadoChange(e.value)}
              />
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={reset}>
            Reiniciar
          </Button>
          <Button size="sm" onClick={() => setOpen(false)}>
            Listo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterRadio({
  label,
  checked,
  onSelect,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5 text-left text-sm transition-colors',
        checked
          ? 'border-foreground bg-foreground/5 text-foreground'
          : 'border-border text-muted-foreground hover:bg-secondary',
      )}
    >
      <span className="truncate">{label}</span>
      {checked && <Check className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden />}
    </button>
  );
}
